import { jsPDF } from "jspdf";
import QRCode from "qrcode";
import { toStreamUrl } from "@/lib/driveUrl";
import { detourLogo } from "@/lib/detour";
import { parseDate } from "@/components/poster/PosterCanvas";

/**
 * Rendu d'export de l'affiche en Canvas 2D natif (sans html2canvas).
 * html2canvas déplace le texte verticalement de façon erratique dans cet environnement ;
 * ici chaque élément est dessiné à des coordonnées exactes, le texte centré au pixel près
 * (textBaseline "middle"). L'export est donc fidèle à la prévisualisation.
 * Base : 700 × 990 (ratio strict A4). Échelle 3,5 ≈ 300 dpi une fois imprimé en A4.
 */
const W = 700;
const H = 990;
const EXPORT_SCALE = 3.5;
const FONT = "Arial, Helvetica, sans-serif";

const imgUrl = (u) => {
  if (!u) return u;
  const streamed = toStreamUrl(u);
  if (streamed !== u) return streamed;
  if (/^https?:\/\//.test(u) && !u.startsWith(process.env.REACT_APP_BACKEND_URL)) {
    return `${process.env.REACT_APP_BACKEND_URL}/api/proxy-image?url=${encodeURIComponent(u)}`;
  }
  return u;
};

function loadImg(src) {
  return new Promise((resolve) => {
    if (!src) return resolve(null);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    // Le même URL déjà chargé sans CORS (vignettes <img> simples) empoisonne le cache
    // navigateur/CDN : on force une requête CORS fraîche pour que le canvas ne soit pas bloqué.
    img.src = src + (src.includes("?") ? "&" : "?") + "_cb=" + Math.random().toString(36).slice(2);
  });
}

/** Rectangle aux coins arrondis asymétriques (rayons [haut-gauche, haut-droit, bas-droit, bas-gauche]). */
function rr(ctx, x, y, w, h, radii) {
  const [tl, tr, br, bl] = radii;
  ctx.beginPath();
  ctx.moveTo(x + tl, y);
  ctx.lineTo(x + w - tr, y);
  ctx.arcTo(x + w, y, x + w, y + tr, tr);
  ctx.lineTo(x + w, y + h - br);
  ctx.arcTo(x + w, y + h, x + w - br, y + h, br);
  ctx.lineTo(x + bl, y + h);
  ctx.arcTo(x, y + h, x, y + h - bl, bl);
  ctx.lineTo(x, y + tl);
  ctx.arcTo(x, y, x + tl, y, tl);
  ctx.closePath();
}

function setFont(ctx, line) {
  ctx.font = `${line.weight} ${line.size}px ${FONT}`;
  try {
    ctx.letterSpacing = line.ls ? `${line.ls}px` : "0px";
  } catch {
    /* letterSpacing non supporté : ignoré */
  }
}

/** Découpe les lignes pour tenir dans maxTextWidth (retour à la ligne par mots). */
function layoutLines(ctx, lines, maxTextWidth) {
  const out = [];
  for (const ln of lines) {
    setFont(ctx, ln);
    const width = ctx.measureText(ln.text).width;
    if (!maxTextWidth || width <= maxTextWidth) {
      out.push({ ...ln, width });
      continue;
    }
    let cur = "";
    for (const word of ln.text.split(" ")) {
      const test = cur ? `${cur} ${word}` : word;
      if (cur && ctx.measureText(test).width > maxTextWidth) {
        out.push({ ...ln, text: cur, width: ctx.measureText(cur).width, gapAfter: 0 });
        cur = word;
      } else {
        cur = test;
      }
    }
    if (cur) out.push({ ...ln, text: cur, width: ctx.measureText(cur).width });
  }
  return out;
}

const lineHeight = (l) => l.size * 1.12;

/**
 * Dessine un cadre de couleur (fond arrondi éventuellement penché) avec son texte
 * parfaitement centré. Renvoie les dimensions du cadre.
 */
function drawFrame(ctx, lines, opts) {
  const { x, y, anchor = "left", fixedW, maxW, minW = 0, padH, padV, radii, rotate = 0, color, alpha = 1, ink } = opts;
  const maxTextW = fixedW ? fixedW - 2 * padH : maxW ? maxW - 2 * padH : 0;
  const laid = layoutLines(ctx, lines, maxTextW);
  const textW = laid.reduce((m, l) => Math.max(m, l.width), 0);
  let w = fixedW || Math.min(Math.max(textW + 2 * padH, minW), maxW || Infinity);
  const contentH = laid.reduce((s, l) => s + lineHeight(l) + (l.gapAfter || 0), 0);
  const h = contentH + 2 * padV;
  const fx = anchor === "right" ? x - w : x;

  ctx.save();
  ctx.translate(fx + w / 2, y + h / 2);
  if (rotate) ctx.rotate((rotate * Math.PI) / 180);
  ctx.globalAlpha = alpha;
  ctx.fillStyle = color;
  rr(ctx, -w / 2, -h / 2, w, h, radii);
  ctx.fill();
  ctx.globalAlpha = 1;
  ctx.fillStyle = ink;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  let cy = -h / 2 + padV;
  for (const l of laid) {
    setFont(ctx, l);
    ctx.fillText(l.text, 0, cy + lineHeight(l) / 2);
    cy += lineHeight(l) + (l.gapAfter || 0);
  }
  ctx.restore();
  return { w, h };
}

/** Photo recadrée pour couvrir un cadre sans déformation (zoom de sécurité overscan). */
function drawCover(ctx, img, x, y, w, h, overscan = 1) {
  const ratio = img.naturalWidth / img.naturalHeight;
  const frameRatio = w / h;
  let iw, ih;
  if (ratio > frameRatio) { ih = h; iw = ih * ratio; } else { iw = w; ih = iw / ratio; }
  iw *= overscan;
  ih *= overscan;
  ctx.drawImage(img, x + (w - iw) / 2, y + (h - ih) / 2, iw, ih);
}

/** Photo secondaire : cadre aux proportions exactes de la photo (recadrage seulement si > maxH). */
function drawFit(ctx, img, x, yBottom, w, maxH, rotate) {
  const ratio = img.naturalWidth / img.naturalHeight;
  let h = Math.round(w / ratio);
  let cropped = false;
  if (h > maxH) { h = maxH; cropped = true; }
  const y = yBottom - h;
  ctx.save();
  ctx.translate(x + w / 2, y + h / 2);
  if (rotate) ctx.rotate((rotate * Math.PI) / 180);
  ctx.beginPath();
  rr(ctx, -w / 2, -h / 2, w, h, [18, 18, 18, 18]);
  ctx.clip();
  if (cropped) drawCover(ctx, img, -w / 2, -h / 2, w, h);
  else ctx.drawImage(img, -w / 2, -h / 2, w, h);
  ctx.restore();
  // bordure blanche
  ctx.save();
  ctx.translate(x + w / 2, y + h / 2);
  if (rotate) ctx.rotate((rotate * Math.PI) / 180);
  ctx.strokeStyle = "rgba(255,255,255,0.9)";
  ctx.lineWidth = 4;
  rr(ctx, -w / 2 + 2, -h / 2 + 2, w - 4, h - 4, [16, 16, 16, 16]);
  ctx.stroke();
  ctx.restore();
  return h;
}

async function renderPoster(poster, scale = EXPORT_SCALE) {
  const p = poster || {};
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(W * scale);
  canvas.height = Math.round(H * scale);
  const ctx = canvas.getContext("2d");
  ctx.scale(scale, scale);

  const theme = p.theme_color || "#3F6B3F";
  const ink = p.dark_text ? "#1E1B18" : "#FFFFFF";
  const dark = p.template === "sombre";
  const pct = (v) => Math.min(140, Math.max(60, Number(v) || 100)) / 100;
  const titleSize = Math.round(44 * pct(p.title_size));
  const title2Size = Math.round(20 * pct(p.title2_size));
  const subtitleSize = Math.round(37 * pct(p.subtitle_size));
  const dateScale = pct(p.date_size);
  const infoSize = Math.round(19 * pct(p.info_size));
  const panelScale = pct(p.panel_size);
  const contactScale = pct(p.contact_size);
  const date = parseDate(p.date);
  const photoUrls = (p.secondary_images || []).filter(Boolean).slice(0, 2).map(imgUrl);
  const logoUrls = (p.logos || []).filter(Boolean).map(imgUrl);
  const hasContact = !!p.contact;
  const hasFooter = logoUrls.length > 0 || !!p.qr_url || hasContact;
  const bottomClearance = hasFooter ? 146 : 24;

  const [mainImg, photos, logos, qrImg] = await Promise.all([
    p.main_image ? loadImg(imgUrl(p.main_image)) : Promise.resolve(null),
    Promise.all(photoUrls.map(loadImg)),
    Promise.all(logoUrls.map((u) => (p.detour_logos ? detourLogo(u).then(loadImg) : loadImg(u)))),
    p.qr_url ? QRCode.toDataURL(p.qr_url, { margin: 0, width: 300 }).then(loadImg) : Promise.resolve(null),
  ]);

  // Fond
  ctx.fillStyle = dark ? "#181C17" : theme;
  ctx.fillRect(0, 0, W, H);
  if (mainImg) {
    drawCover(ctx, mainImg, 0, 0, W, H, 1.07);
    if (dark) {
      ctx.fillStyle = "rgba(8, 10, 8, 0.45)";
      ctx.fillRect(0, 0, W, H);
    }
  }

  // Bloc date
  if (date) {
    const lines = [
      { text: String(date.year), size: Math.round(22 * dateScale), weight: 800, ls: 3, gapAfter: 4 },
      { text: String(date.day), size: Math.round(68 * dateScale), weight: 900, gapAfter: 6 },
      { text: date.month, size: Math.round(19 * dateScale), weight: 800, ls: 1, gapAfter: p.time ? 10 : 0 },
    ];
    if (p.time) lines.push({ text: p.time, size: Math.round(22 * dateScale), weight: 800 });
    drawFrame(ctx, lines, { x: 24, y: 24, minW: 168, padH: 20, padV: 16, radii: [26, 34, 24, 38], rotate: -1.5, color: theme, ink });
  }

  // Titre principal (+ ligne secondaire dans le même cartouche)
  if (p.title) {
    const titleLines = [{ text: p.title.toUpperCase(), size: titleSize, weight: 900, gapAfter: 0 }];
    if (p.title_2) {
      titleLines[0].gapAfter = 8;
      String(p.title_2).split("\n").forEach((t) => titleLines.push({ text: t, size: title2Size, weight: 800, gapAfter: 3 }));
      titleLines[titleLines.length - 1].gapAfter = 0;
    }
    drawFrame(ctx, titleLines, {
      x: W - 24, y: 24, anchor: "right", maxW: 470, padH: 28, padV: 18, radii: [22, 30, 24, 26], rotate: 1, color: theme, ink,
    });
  }

  // Sous-titre / nom de l'événement
  if (p.subtitle) {
    drawFrame(ctx, [{ text: p.subtitle.toUpperCase(), size: subtitleSize, weight: 900, gapAfter: 0 }], {
      x: 24, y: 440, maxW: 460, padH: 24, padV: 12, radii: [20, 26, 28, 22], rotate: -1.8, color: theme, ink,
    });
  }

  // Photos secondaires + informations complémentaires (colonne bas-gauche, ancrée en bas)
  const okPhotos = photos.filter(Boolean);
  if (okPhotos.length > 0 || p.info) {
    const photoW = okPhotos.length > 1 ? 158 : 320;
    const photoHeights = okPhotos.map((img) => Math.min(220, Math.round(photoW / (img.naturalWidth / img.naturalHeight))));
    const photosH = photoHeights.length ? Math.max(...photoHeights) : 0;
    // mesure du bloc info pour ancrer la colonne en bas
    let infoH = 0;
    let infoLines = [];
    if (p.info) {
      infoLines = layoutLines(ctx, String(p.info).split("\n").map((t) => ({ text: t, size: infoSize, weight: 800, gapAfter: 4 })), 0);
      infoH = infoLines.reduce((s, l) => s + lineHeight(l) + (l.gapAfter || 0), 0) - 4 + 24;
    }
    const colBottom = H - bottomClearance;
    let cursor = colBottom;
    if (p.info) {
      cursor -= infoH;
      drawFrame(ctx, String(p.info).split("\n").map((t) => ({ text: t, size: infoSize, weight: 800, gapAfter: 4 })), {
        x: 24, y: cursor, maxW: 340, padH: 20, padV: 12, radii: [22, 26, 20, 28], rotate: 1.2, color: theme, ink,
      });
      cursor -= 14;
    }
    if (okPhotos.length > 0) {
      const yBottom = cursor;
      okPhotos.forEach((img, i) => {
        drawFit(ctx, img, 24 + i * (photoW + 14), yBottom, photoW, 220, okPhotos.length > 1 ? (i === 0 ? -2 : 1.6) : -1);
      });
    }
  }

  // Tarifs et lieu (panneau bas-droite)
  if (p.prices || p.venue_name || p.venue_address) {
    const lines = [];
    if (p.prices) String(p.prices).split("\n").forEach((t) => lines.push({ text: t, size: Math.round(18 * panelScale), weight: 900, gapAfter: 5 }));
    if (lines.length) lines[lines.length - 1].gapAfter = 14;
    if (p.venue_name) lines.push({ text: p.venue_name, size: Math.round(16 * panelScale), weight: 800, gapAfter: 4 });
    if (p.venue_address) String(p.venue_address).split("\n").forEach((t) => lines.push({ text: t, size: Math.round(14 * panelScale), weight: 700, gapAfter: 3 }));
    if (lines.length) lines[lines.length - 1].gapAfter = 0;
    // hauteur pour ancrer en bas
    const laid = layoutLines(ctx, lines, 246 - 28);
    const panelH = laid.reduce((s, l) => s + lineHeight(l) + (l.gapAfter || 0), 0) + 32;
    drawFrame(ctx, lines, {
      x: W - 24, y: H - bottomClearance - panelH, anchor: "right", fixedW: 246, padH: 14, padV: 16, radii: [26, 22, 30, 24], rotate: -0.6, color: theme, alpha: 0.94, ink,
    });
  }

  // Pied de page : vague couleur thème — logos, contact et QR sur UNE SEULE LIGNE
  // (auto-dimensionnement : logos puis QR réduits jusqu'à ce que tout rentre dans les 652 px utiles)
  if (hasFooter) {
    const okLogos = logos.filter(Boolean);
    const detour = !!p.detour_logos;
    const AVAIL = 652; // 700 - 2×24 de marges

    let contactLines = [];
    if (hasContact) {
      contactLines = [
        { text: "Informations et réservations", size: Math.round(14 * contactScale), weight: 800, gapAfter: 3 },
        ...String(p.contact).split("\n").map((t) => ({ text: t, size: 12.5 * contactScale, weight: 700, gapAfter: 2 })),
      ];
      contactLines[contactLines.length - 1].gapAfter = 0;
    }

    // Zone contact : largeur mesurée de sa plus longue ligne
    let contactW = 0;
    if (contactLines.length) {
      const laidC = layoutLines(ctx, contactLines, 300);
      contactW = laidC.reduce((m, l) => Math.max(m, l.width), 0) + 20;
    }

    // QR : taille réduite si la ligne est chargée
    let qrTile = qrImg ? (okLogos.length >= 3 ? 76 : okLogos.length >= 2 && hasContact ? 86 : 94) : 0;
    const qrGap = qrImg ? 20 : 0;

    // Logos : taille réduite tant que la ligne dépasse
    let logoH = 52;
    let logoItems = [];
    const measureLogos = (h) => {
      const items = okLogos.map((img) => {
        const ih = detour ? Math.round(h * 1.27) : h;
        let iw = Math.round(ih * (img.naturalWidth / img.naturalHeight));
        if (!detour && iw > Math.max(80, h * 2.3)) iw = Math.round(Math.max(80, h * 2.3));
        return { img, iw, ih, tileW: detour ? iw : iw + 10, tileH: detour ? ih : ih + 10 };
      });
      const w = items.reduce((s, it, i) => s + it.tileW + (i > 0 ? 16 : 0), 0);
      return { items, w };
    };
    let { items, w: logosW } = measureLogos(logoH);
    while (logosW + contactW + qrTile + qrGap > AVAIL && logoH > 30) {
      logoH -= 3;
      ({ items, w: logosW } = measureLogos(logoH));
    }
    // S'il reste trop serré, on réduit aussi le QR
    while (logosW + contactW + qrTile + qrGap > AVAIL && qrTile > 56) {
      qrTile -= 6;
    }
    logoItems = items;

    const contactH = contactLines.length ? layoutLines(ctx, contactLines, 300).reduce((s, l) => s + lineHeight(l) + (l.gapAfter || 0), 0) : 0;
    const logosH = logoItems.length ? Math.max(...logoItems.map((it) => it.tileH)) : 0;
    const contentH = Math.max(logosH, contactH, qrTile, 60);
    const boxH = 34 + contentH + 14;
    const waveTop = H - boxH + 10;

    ctx.fillStyle = theme;
    ctx.beginPath();
    ctx.moveTo(-20, H + 12);
    ctx.lineTo(-20, waveTop + 44);
    ctx.quadraticCurveTo(-20, waveTop, W / 2, waveTop);
    ctx.quadraticCurveTo(W + 20, waveTop, W + 20, waveTop + 36);
    ctx.lineTo(W + 20, H + 12);
    ctx.closePath();
    ctx.fill();

    const cy = waveTop + 44 + (H - 8 - (waveTop + 44)) / 2;

    // logos alignés à gauche
    let lx = 24;
    for (const { img, iw, ih, tileW, tileH } of logoItems) {
      const ty = cy - tileH / 2;
      if (tileH !== ih) {
        ctx.fillStyle = "#FFFFFF";
        rr(ctx, lx, ty, tileW, tileH, [10, 10, 10, 10]);
        ctx.fill();
      }
      ctx.drawImage(img, lx + (tileW - iw) / 2, ty + (tileH - ih) / 2, iw, ih);
      lx += tileW + 16;
    }

    // QR sur pastille blanche, légèrement penché
    if (qrImg) {
      const tx = 676 - qrTile;
      ctx.save();
      ctx.translate(tx + qrTile / 2, cy);
      ctx.rotate((2 * Math.PI) / 180);
      ctx.fillStyle = "#FFFFFF";
      rr(ctx, -qrTile / 2, -qrTile / 2, qrTile, qrTile, [14, 14, 14, 14]);
      ctx.fill();
      const inner = qrTile - 14;
      ctx.drawImage(qrImg, -inner / 2, -inner / 2, inner, inner);
      ctx.restore();
    }

    // contact centré dans l'espace restant
    if (contactLines.length) {
      const laid = layoutLines(ctx, contactLines, 300);
      const totalH = laid.reduce((s, l) => s + lineHeight(l) + (l.gapAfter || 0), 0);
      let ty = cy - totalH / 2;
      ctx.fillStyle = ink;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      const leftEdge = logoItems.length ? lx : 24;
      const rightEdge = qrImg ? 676 - qrTile - 20 : 676;
      const cxContact = (leftEdge + rightEdge) / 2;
      for (const l of laid) {
        setFont(ctx, l);
        ctx.fillText(l.text, cxContact, ty + lineHeight(l) / 2);
        ty += lineHeight(l) + (l.gapAfter || 0);
      }
    }
  }

  return canvas;
}

function download(href, filename) {
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/** Rendu léger de l'affiche en image JPEG (instantané affiché sur le site, stocké côté serveur). */
export async function renderPosterBlob(poster, scale = 1.4) {
  const canvas = await renderPoster(poster, scale);
  return new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("Rendu impossible"))), "image/jpeg", 0.85)
  );
}

/** Export PNG haute résolution (réseaux sociaux, partage rapide). */
export async function exportPosterPng(poster, filename = "affiche.png") {
  const canvas = await renderPoster(poster);
  const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
  const url = URL.createObjectURL(blob);
  download(url, filename);
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

/** Export PDF A4 pleine page, prêt pour l'impression. */
export async function exportPosterPdf(poster, filename = "affiche.pdf") {
  const canvas = await renderPoster(poster);
  const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4", compress: true });
  pdf.addImage(canvas.toDataURL("image/jpeg", 0.95), "JPEG", 0, 0, 210, 297);
  pdf.save(filename);
}
